import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link as ScrollLink } from 'react-scroll';
import CrimsonLogo from '../assets/logos/crimson-logo.svg';

const Navbar = ({ sections }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileAnchorEl, setMobileAnchorEl] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMenuOpen = (event) => {
    setMobileAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMobileAnchorEl(null);
  };

  return (
    <AppBar
      position="fixed"
      color="transparent"
      elevation={scrolled ? 8 : 3}
      sx={{
        backgroundColor: scrolled ? 'rgba(14, 14, 14, 0.92)' : 'rgba(14, 14, 14, 0.78)',
        backdropFilter: 'blur(14px)',
        transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
        borderBottom: 'none',
        borderRadius: 0,
        boxShadow: 'none',
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: { xs: 64, md: 72 },
          px: { xs: 2, sm: 4 },
          py: 0,
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            maxWidth: '1200px',
            mx: 'auto',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="img"
              src={CrimsonLogo}
              alt="Crimson Club of Mexico logo"
              sx={{ height: { xs: 34, md: 40 }, filter: 'brightness(1.1)' }}
            />
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                letterSpacing: { xs: 0.3, md: 1 },
                display: { xs: 'none', sm: 'block' },
                color: 'rgba(255,255,255,0.88)',
              }}
            >
              Crimson Club of Mexico
            </Typography>
          </Box>

          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {sections.map((section) => (
                <Button
                  key={section.id}
                  component={ScrollLink}
                  to={section.id}
                  smooth
                  duration={500}
                  spy
                  offset={-90}
                  activeClass="active-section"
                  sx={{
                    color: 'rgba(255,255,255,0.82)',
                    px: 2.5,
                    fontWeight: 600,
                    borderRadius: 999,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.12)',
                    },
                    '&.active-section': {
                      color: theme.palette.primary.contrastText,
                      backgroundColor: 'rgba(255,255,255,0.18)',
                    },
                  }}
                >
                  {section.label}
                </Button>
              ))}
            </Box>
          )}

          {isMobile && (
            <IconButton
              size="large"
              color="inherit"
              aria-label="open navigation menu"
              onClick={handleMenuOpen}
              sx={{
                color: 'rgba(255,255,255,0.95)',
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              <MenuIcon sx={{ fontSize: 28 }} />
            </IconButton>
          )}
        </Box>
      </Toolbar>

      <Menu
        anchorEl={mobileAnchorEl}
        open={Boolean(mobileAnchorEl)}
        onClose={handleMenuClose}
        disableScrollLock
        PaperProps={{
          elevation: 6,
          sx: {
            mt: 1,
            minWidth: 200,
            backgroundColor: 'rgba(14,14,14,0.95)',
            color: 'common.white',
            backdropFilter: 'blur(12px)',
          },
        }}
      >
        {sections.map((section) => (
          <MenuItem
            key={section.id}
            onClick={() => {
              handleMenuClose();
              const target = document.getElementById(section.id);
              if (target) {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
          >
            {section.label}
          </MenuItem>
        ))}
      </Menu>
    </AppBar>
  );
};

export default Navbar;
